import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { EditorContent, useEditor } from '@tiptap/react';
import {
  getSelectedHTML,
  sanitiseEditorTipTap,
} from '../../../kids/utils/editor.utils';
import { cn } from '../../../../../../utilities/cn';
import type { PdfElement } from '../../types/KidPdfGeneratorV2.types';
import {
  convertLegacyHTMLToTipTap,
  RichTextBubbleMenu,
} from './richTextEditor.shared';
export type RichTextFieldPaths = {
  content: string;
  formatOptions: string;
};

interface TextInputTipTapProps {
  field: PdfElement;
  index: number;
  /** Override default `elements.${index}.*` react-hook-form field paths */
  fieldPaths?: RichTextFieldPaths;
  allowStyling?: boolean; // If false, only allows text editing without any styling
  customClassName?: string; // Optional custom className for the editor content
  isReadOnly?: boolean;
  /** Omit outer border/radius when nested inside a bordered container */
  embedded?: boolean;
}

const TextInputTipTap: FunctionComponent<TextInputTipTapProps> = ({
  field,
  index = '',
  fieldPaths,
  allowStyling = true, // Default to true for backward compatibility
  customClassName,
  isReadOnly = false,
  embedded = false,
}) => {
  const contentPath =
    fieldPaths?.content ?? `elements.${index}.content`;
  const formatOptionsPath =
    fieldPaths?.formatOptions ?? `elements.${index}.format_options`;
  const elementTriggerPath = contentPath.replace(/\.content$/, '') || contentPath;

  // Track selected HTML to check formatting
  const [selectedHTML, setSelectedHTML] = useState<string>('');

  const { control, setValue, trigger } = useFormContext();
  const watchedContent = useWatch({
    name: contentPath,
    control,
  });
  const formatOptions = useWatch({
    name: formatOptionsPath,
    control,
  });

  const resolvedContent =
    typeof watchedContent === 'string'
      ? watchedContent
      : typeof field.content === 'string'
        ? field.content
        : '';

  const convertLegacyHTML = convertLegacyHTMLToTipTap;

  const extractPlainText = (html: unknown): string => {
    if (typeof html !== 'string' || !html) return '';

    return html.replace(/<[^>]*>/g, '').trim();
  };

  const textToHTML = (text: unknown): string => {
    if (typeof text !== 'string' || !text) return '<p></p>';
    return `<p>${text}</p>`;
  };

  const contentPathRef = useRef(contentPath);
  const elementTriggerPathRef = useRef(elementTriggerPath);
  contentPathRef.current = contentPath;
  elementTriggerPathRef.current = elementTriggerPath;

  const extensions = useMemo(
    () =>
      allowStyling
        ? [
            StarterKit,
            TextStyle,
            Color,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Highlight.configure({ multicolor: true }),
          ]
        : [
            StarterKit.configure({
              bold: false,
              italic: false,
              strike: false,
              code: false,
              heading: false,
              blockquote: false,
              horizontalRule: false,
              hardBreak: false,
            }),
          ],
    [allowStyling],
  );

  const editor = useEditor({
    editable: !isReadOnly,
    extensions,
    content: allowStyling
      ? convertLegacyHTML(resolvedContent)
      : textToHTML(extractPlainText(resolvedContent)),
    onUpdate: ({ editor }) => {
      const activeContentPath = contentPathRef.current;
      const activeElementTriggerPath = elementTriggerPathRef.current;

      if (allowStyling) {
        const html = editor.getHTML();
        const sanitised = sanitiseEditorTipTap(html);
        setValue(activeContentPath, sanitised);
      } else {
        const plainText = editor.getText().trim();
        const sanitised = textToHTML(plainText);
        setValue(activeContentPath, sanitised);
      }
      trigger(activeElementTriggerPath);
    },
    editorProps: {
      attributes: {
        class: customClassName || 'min-h-24 outline-none p-2 text-gray-900',
      },
      handleDOMEvents: {
        keydown: (_view, event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            return true; // stop Enter from inserting a newline or hard break
          }
          return false;
        },
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!isReadOnly);
  }, [editor, isReadOnly]);

  // Apply block-level text alignment from format_options
  useEffect(() => {
    if (editor && formatOptions?.textAlign && allowStyling) {
      const alignment = formatOptions.textAlign as 'left' | 'center' | 'right';
      // Apply alignment via CSS to the editor container (affects all content)
      const editorElement = editor.view.dom as HTMLElement;
      if (editorElement) {
        editorElement.style.textAlign = alignment;
      }
      // Also apply to all paragraphs and headings via TipTap commands (only if TextAlign extension is enabled)
      if (editor.commands.setTextAlign) {
        editor.commands.setTextAlign(alignment);
      }
    }
  }, [editor, formatOptions?.textAlign, allowStyling]);

  // Apply block-level font size from format_options
  useEffect(() => {
    if (editor && formatOptions?.fontSize) {
      const fontSize = formatOptions.fontSize;
      // Apply font size via CSS to the editor container (affects all content)
      const editorElement = editor.view.dom as HTMLElement;
      if (editorElement) {
        editorElement.style.fontSize = `${fontSize}px`;
      }
    }
  }, [editor, formatOptions?.fontSize]);

  useEffect(() => {
    if (!editor || !allowStyling) return;

    const updateSelection = () => {
      const html = getSelectedHTML(editor);
      setSelectedHTML(html);
    };

    // Update on selection change
    editor.on('selectionUpdate', updateSelection);
    editor.on('update', updateSelection);

    return () => {
      editor.off('selectionUpdate', updateSelection);
      editor.off('update', updateSelection);
    };
  }, [editor, allowStyling]);

  // Update editor content when field content changes (for non-styling mode)
  useEffect(() => {
    if (!editor || allowStyling) return;

    const plainText = extractPlainText(resolvedContent);
    const currentText = editor.getText().trim();
    if (plainText !== currentText) {
      editor.commands.setContent(textToHTML(plainText), { emitUpdate: false });
    }
  }, [resolvedContent, editor, allowStyling]);



  return (
    <Controller
      name={contentPath}
      control={control}
      render={({ field, fieldState }) => (
        <div className="relative">
          {/* --- Bubble Menu --- */}
          {editor && allowStyling && !isReadOnly && (
            <RichTextBubbleMenu
              editor={editor}
              selectedHTML={selectedHTML}
              menuId={index}
            />
          )}

          {/* --- Editable Area --- */}
          <EditorContent
            editor={editor}
            onBlur={() => trigger(contentPath)}
            className={cn(
              'w-full outline-none',
              embedded
                ? isReadOnly
                  ? 'cursor-not-allowed bg-gray-50'
                  : 'cursor-text bg-white'
                : customClassName
                  ? `rounded border ${isReadOnly ? 'cursor-not-allowed bg-gray-50' : 'cursor-text bg-white'}`
                  : `min-h-24 rounded border p-2 text-gray-900 ${isReadOnly ? 'cursor-not-allowed bg-gray-50' : 'cursor-text bg-white'}`,
              !embedded &&
                (fieldState.error
                  ? 'border-red-500 hover:border-red-600 focus:border-red-500'
                  : 'border-gray-300 hover:border-blue-500 focus:border-blue-500'),
            )}
            style={{
              textAlign: formatOptions?.textAlign || 'left',
            }}
          />
          {fieldState.error && (
            <p className="mt-1 text-xs text-red-600">
              {fieldState.error.message}
            </p>
          )}

        </div>
      )}
    />
  );
};

export default TextInputTipTap;
